import React, { useEffect, useMemo, useCallback, useState } from "react"
import { useSelector } from "react-redux"
import { IAppState } from "../../app/appTypes"
import { RoutePaths } from "../../app/routes/Routes"
import SignInForm from "../../features/account/components/SignInForm"
import history from "../../app/routes/history"
import BrowserWarning from "../../shared/components/browser/BrowserWarning"
import { useAppDispatch } from "../../shared/hooks"
import { displayUserMessageAction } from "../../features/userMessage/state/userMessageActions"
import { UserMessageTypes } from "../../features/userMessage/state/userMessageTypes"
import Button from "../../shared/components/buttons/Button"
import { UserManager } from "oidc-client-ts"
import { setOAuthIdToken } from "../../app/state/appSessionStorage"
import { fetchCurrentUserAction } from "../../features/user/state/userActions"
import { AccountActionTypes } from "../../features/account/state/accountTypes"
import { getUserSession } from "../../shared/selectors/user"
import { SignInType } from "../../features/user/state/userTypes"
import LoadingSpinner from "../../shared/components/ui/LoadingSpinner"

const url = window.location.origin

export const settings = {
  authority: "",
  client_id: "",
  redirect_uri: url + "/",
  post_logout_redirect_uri: url + "/signin",
  response_type: "code",
  scope: "openid profile email",
  popup_redirect_uri: url + "/signin",
  popup_post_logout_redirect_uri: url + "/signin",
  silent_redirect_uri: url + "/signin",
  loadUserInfo: true,
  prompt: "select_account",
}

function buildUserManager(authority: string, clientId: string) {
  if (!(authority && clientId)) return null
  return new UserManager({ ...settings, authority, client_id: clientId })
}

const SignInPage: React.FC = (): JSX.Element => {
  const apiToken = useSelector((state: IAppState) => state.userSession.apiToken)
  const dispatch = useAppDispatch()
  const search = window.location.search
  const params = new URLSearchParams(search)
  const { signInType, oauthAuthority, oauthClientId } = useSelector(getUserSession)
  const authority = localStorage.getItem("oauthAuthority")
  const clientId = localStorage.getItem("oauthClientId")
  const [processingOauth, setProcessingOauth] = useState(false)

  const userManager = useMemo(
    () => buildUserManager(authority || oauthAuthority, clientId || oauthClientId),
    [oauthAuthority, oauthClientId]
  )

  const _signInOIDC = () => {
    if (!userManager) return
    setTimeout(() => setProcessingOauth(true), 0)
    userManager.signinPopup().then((user) => {
      setOAuthIdToken(user.id_token)
      dispatch({
        type: AccountActionTypes.SIGN_IN_SWAP_OAUTH_TOKEN
      })

      dispatch(fetchCurrentUserAction())
      history.push(RoutePaths.ROOT)
    }).catch(() => {
      setProcessingOauth(false)
      history.replace(RoutePaths.SIGN_IN)
      console.error("OIDC login failed")
    })
  }

  const signInOIDC = useCallback(_signInOIDC, [oauthAuthority, oauthClientId])

  useEffect(() => {
    if (oauthAuthority) localStorage.setItem("oauthAuthority", oauthAuthority)
    if (oauthClientId) localStorage.setItem("oauthClientId", oauthClientId)
  }, [oauthAuthority])

  useEffect(() => {
    if (apiToken) {
      history.replace(RoutePaths.ROOT)
    }
  }, [apiToken])

  useEffect(() => {
    if (window.location.pathname == "/sso") {
      signInOIDC()
    }
  }, [])

  useEffect(() => {
    const userMessage = params.get("userMessage")
    if (!userMessage) return
    const message = {
      messageKey: userMessage,
      type: UserMessageTypes.SUCCESS
    }
    dispatch(displayUserMessageAction(message))
    setTimeout(() => history.replace(RoutePaths.SIGN_IN), 0)
  }, [])

  useEffect(() => {
    const userMessage = params.get("userError")
    if (!userMessage) return
    const message = {
      messageKey: userMessage,
      type: UserMessageTypes.ERROR
    }
    dispatch(displayUserMessageAction(message))
    setTimeout(() => history.replace(RoutePaths.SIGN_IN), 0)
  }, [])

  useEffect(() => {
    const code = params.get("code")
    if (!code) return
    // this is needed to handle the popup callback correctly.
    userManager.signinPopupCallback().then(
      (user) => { console.log("callback for", user) }
    ).catch((e) => console.error("error", e))
  }, [])

  useEffect(() => {
    if (signInType === SignInType.Oauth) {
      signInOIDC()
    }
  }, [signInType])

  const OauthMessage: React.FC = () => {
    return <div>
      <LoadingSpinner />
      <div className="text-center mb-5">Awaiting SSO</div>
    </div>
  }
  return (
    <div>
      <BrowserWarning />
      {processingOauth ? <OauthMessage /> : <SignInForm />}
      <div>
        {oauthAuthority ?
        <>
          <Button action={signInOIDC}> Login with OIDC </Button>
        </>
          : null}
      </div>
    </div>
  )
}


export default SignInPage
